import type { CartItem } from "@/types";

/**
 * Round a currency amount to 2 decimals, half-up, after snapping it to the
 * micro-real grid the merchant validator rounds on, so float noise such as
 * 3.005 → 300.49999 cents still rounds up to 3.01 like the server.
 */
export function roundCurrency(value: number): number {
  const cents = Number(((Number(value) || 0) * 100).toFixed(4));
  return Math.round(cents) / 100;
}

/**
 * Exact sum of the cart lines' totals (full precision, no rounding). The cart
 * total is always derived from the lines so it can never drift from them.
 */
export function sumCartItems(items: CartItem[] | null | undefined): number {
  return (items ?? []).reduce((sum, item) => sum + (Number(item.itemTotalPrice) || 0), 0);
}

export interface OrderTotalsInput {
  /** Sum of cart item totals (full precision). */
  itemsPrice: number;
  /** Delivery / service fee. */
  servicePrice: number;
  couponPrice?: number;
  loyaltyPoints?: number;
  benefitTotal?: number;
}

export interface OrderTotals {
  itemsPrice: number;
  servicePrice: number;
  totalDiscount: number;
  /** items + service − discounts, floored at 0 — never negative on the wire. */
  orderAmount: number;
}

/**
 * Final, rounded order totals computed once at checkout. The order amount is
 * floored at 0 so stacked discounts (coupon + loyalty + benefits) can never
 * produce a negative charge, and every value is rounded exactly here.
 */
export function computeOrderTotals(input: OrderTotalsInput): OrderTotals {
  const itemsPrice = roundCurrency(input.itemsPrice);
  const servicePrice = roundCurrency(input.servicePrice);
  const totalDiscount = roundCurrency(
    Math.max(0, (input.couponPrice ?? 0) + (input.loyaltyPoints ?? 0) + (input.benefitTotal ?? 0)),
  );
  const orderAmount = roundCurrency(Math.max(0, itemsPrice + servicePrice - totalDiscount));
  return { itemsPrice, servicePrice, totalDiscount, orderAmount };
}
